/**
 * Gates authenticated routes behind a /api/me check. On 401 the api()
 * helper redirects to /login, so this just waits for the answer and either
 * renders the protected page (Outlet) or nothing (during the redirect).
 *
 * Mandatory MFA: when /api/me reports mfa_gate (the session is OTP-only but
 * one of the user's orgs has servers), every other /api/* call would 403, so
 * bounce straight to /mfa-verify. The api() helper catches in-flight 403s
 * the same way.
 */

import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router';
import { api } from '@/lib/api';
import { OrgProvider } from '@/contexts/OrgContext';

interface MeResponse {
  id: string;
  email: string;
  name: string | null;
  is_admin: boolean;
  active_org_id: string | null;
  mfa_enabled?: boolean;
  aal?: 'aal1' | 'aal2';
  mfa_gate?: boolean;
}

export default function RequireAuth() {
  const location = useLocation();
  const { data, isPending, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isPending) return null;
  if (isError) return null; // api() already redirected

  if (data?.mfa_gate) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/mfa-verify?next=${next}`} replace />;
  }

  return (
    <OrgProvider>
      <Outlet />
    </OrgProvider>
  );
}
